import Downshift, { GetInputPropsOptions } from 'downshift';
import { map } from 'lodash';
import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { getFilteredOrderedOptions } from '../../../helpers/searchHelpers';
import { colourNeutral09 } from '../../../styling/colours';
import { fontFamily, fontSize03 } from '../../../styling/fonts';
import { spacing01, spacing02, spacing11 } from '../../../styling/spacing';
import { TextInput } from '../../TextInput';
import { ValidityProps } from '../FormField';
import { inputBorderWidth } from './inputStyles';
import { getOptionsByKey, SelectOption } from './Select';

export type SearchSelectOption<TValue> = SelectOption<TValue> & {
  searchableText?: string;
};

export type SearchSelectOptions<TValue> = Array<SearchSelectOption<TValue>>;

type Props<TValue> = {
  onChange: (newValue: TValue | null) => void;
  onBlur?: () => void;
  value: TValue | null;
  options: SearchSelectOptions<TValue>;
  mapValueToKey?: (value: TValue) => string;
  name?: string;
  disabled?: boolean;
  placeholder?: string;
  minimumSearchCharacters?: number;
  testId?: string;
} & ValidityProps;

export const SearchSelect = <TValue,>({
  onChange,
  onBlur,
  value,
  options,
  mapValueToKey = val => `${val}`,
  name,
  $isValid,
  $isInvalid,
  disabled,
  placeholder,
  testId,
  ...props
}: Props<TValue>) => {
  const onBlurWrapper = (inputProps: GetInputPropsOptions) => {
    return (event: React.FocusEvent<HTMLInputElement>) => {
      if (inputProps.onBlur != null) {
        inputProps.onBlur(event);
      }
      if (onBlur != null) {
        onBlur();
      }
    };
  };

  const optionsByKey = useMemo(() => getOptionsByKey(options, mapValueToKey), [options, mapValueToKey]);

  const itemToString = (item: TValue | null) => (item != null ? optionsByKey[mapValueToKey(item)].text : '');

  const getFilteredSearchOptions = (inputValue: string | null) =>
    getFilteredOrderedOptions(inputValue, options, option => option.searchableText || option.text);

  const minimumSearchCharacters = props.minimumSearchCharacters || 0;

  return (
    <Downshift onChange={onChange} itemToString={itemToString} selectedItem={value}>
      {({ getInputProps, getItemProps, highlightedIndex, isOpen, inputValue }) => {
        const inputProps = getInputProps();

        return (
          <div>
            <Container>
              <TextInput
                {...getInputProps()}
                // overriding Downshift behaviour for Home and End
                onKeyDown={event => {
                  if (!(event.key === 'Home' || event.key === 'End')) {
                    if (inputProps.onKeyDown) {
                      inputProps.onKeyDown(event);
                    }
                  }
                }}
                onBlur={onBlurWrapper(inputProps)}
                $isValid={$isValid}
                $isInvalid={$isInvalid}
                name={name}
                data-testid={testId || searchSelectTestId(name || '')}
                disabled={disabled}
                placeholder={placeholder}
              />
              {isOpen && (inputValue || '').length >= minimumSearchCharacters ? (
                <OptionsMenu>
                  {map(getFilteredSearchOptions(inputValue), (option, index) => (
                    <Option
                      key={mapValueToKey(option.value)}
                      {...getItemProps({
                        item: option.value,
                        index,
                      })}
                      $isHighlighted={highlightedIndex === index}
                      data-testid={searchSelectOptionTestId(name || '', option.value)}
                    >
                      {option.text}
                    </Option>
                  ))}
                </OptionsMenu>
              ) : null}
            </Container>
          </div>
        );
      }}
    </Downshift>
  );
};

export const searchSelectTestId = (fieldName?: string) => `${fieldName || ''}.searchSelectInput`;

export const searchSelectOptionTestId = <TValue,>(fieldName: string, value: TValue) =>
  `${fieldName}.searchSelectOption.${JSON.stringify(value)}`;

const OptionsMenu = styled.div`
  overflow-y: auto;
  max-height: ${spacing11};
  position: absolute;
  width: calc(100% - 2 * ${inputBorderWidth});
  z-index: 1;
  text-overflow: ellipsis;
  border: solid ${colourNeutral09};
  border-width: 0 ${inputBorderWidth} ${inputBorderWidth} ${inputBorderWidth};
`;

const Container = styled.div`
  width: 100%;
`;

const Option = styled.div<{ $isHighlighted: boolean }>`
  font-family: ${fontFamily};
  padding: ${spacing01} ${spacing02};
  font-size: ${fontSize03};
  background-color: ${props => (props.$isHighlighted ? colourNeutral09 : 'white')};
`;
